import { useState, useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBrandingStore } from '@/stores/branding'
import { Sidebar } from './Sidebar'
import type { NavItemConfig } from './SidebarNavItem'

interface Props {
  navItems: NavItemConfig[]
  children: ReactNode
}

const STORAGE_KEY = 'arquitecto-sidebar-collapsed'

/**
 * AppShell — layout MOBILE-FIRST con sidebar.
 *
 * - Móvil (<sm): la sidebar es OFF-CANVAS (drawer con overlay); el contenido es
 *   full-width (sin margen) y una mini-topbar lleva el hamburguesa + branding. El
 *   chat queda lo más diáfano posible (sin sidebar robando ancho ni cromo de escritorio).
 * - Escritorio (sm+): sidebar fija 256/64px colapsable (comportamiento previo intacto).
 *
 * §6.1 plan PWA premium + PWA-3 mobile-first.
 */
export function AppShell({ navItems, children }: Props) {
  const branding = useBrandingStore((s) => s.activo)
  const location = useLocation()
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    try { return window.localStorage.getItem(STORAGE_KEY) === '1' } catch { return false }
  })
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0') } catch { /* ignore */ }
  }, [collapsed])

  useEffect(() => {
    function onToggle() { setCollapsed((v) => !v) }
    window.addEventListener('arquitecto:toggle-sidebar', onToggle)
    return () => window.removeEventListener('arquitecto:toggle-sidebar', onToggle)
  }, [])

  // Cerrar el drawer móvil al navegar (no dejar la sidebar abierta tras elegir destino).
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  return (
    <div className="flex min-h-[100dvh] bg-background">
      {/* Overlay del drawer en móvil */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm sm:hidden" onClick={() => setMobileOpen(false)} aria-hidden />
      )}

      <Sidebar
        navItems={navItems}
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col transition-[margin] duration-300',
          collapsed ? 'sm:ml-16' : 'sm:ml-64', // margen SOLO en escritorio; en móvil full-width
        )}
        style={{ transitionTimingFunction: 'var(--ease-premium)' }}
      >
        {/* Mini-topbar móvil: hamburguesa + branding (en escritorio no aparece). */}
        <div className="flex items-center gap-2.5 px-3 py-2 sm:hidden">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Menu className="h-5 w-5" />
          </button>
          <img src={branding.logo_url || '/aarpia-eagle-mark.png'} alt="" className="h-7 w-7 object-contain" />
          <span className="truncate text-sm font-semibold">{branding.nombre_comercial || 'Arquitecto'}</span>
        </div>

        <main className="flex-1 overflow-y-auto p-3 sm:px-6 sm:py-8">{children}</main>
      </div>
    </div>
  )
}
